import { isSpecifiedRowAnswer } from "./specify/core";
import type { SpecifiedRowAnswer } from "./specify/types";

export type TaggedChoice = { option: number } | { custom: string };
export type ChoiceValue = string | number | TaggedChoice;
export type AnswerValue = string | TaggedChoice;
export type AnswerRow = AnswerValue | readonly AnswerValue[];
export type AnswerCell = AnswerRow | SpecifiedRowAnswer<AnswerRow>;
export type AnswerJson =
  | AnswerValue
  | readonly (AnswerCell | readonly AnswerCell[])[];

export const isTaggedChoice = (value: unknown): value is TaggedChoice => {
  if (
    typeof value !== "object" ||
    value === null ||
    Object.keys(value).length !== 1
  )
    return false;
  return (
    ("option" in value &&
      Number.isInteger(value.option) &&
      Number(value.option) > 0) ||
    ("custom" in value &&
      typeof value.custom === "string" &&
      value.custom.length > 0)
  );
};

export const isChoiceValue = (value: unknown): value is ChoiceValue =>
  typeof value === "string" ||
  typeof value === "number" ||
  isTaggedChoice(value);

export const readPreviousAnswer = <T>(
  structured: unknown,
  legacy: T,
  valid: (value: unknown) => value is T,
): T => {
  if (structured === undefined) return legacy;
  if (!valid(structured)) throw new Error("Invalid previous_answer_json.");
  return structured;
};

export const isChoiceList = (value: unknown): value is ChoiceValue[] =>
  Array.isArray(value) && value.every(isChoiceValue);

export type ChoiceRow = ChoiceValue | ChoiceValue[];
export type SavedChoiceRow = ChoiceRow | SpecifiedRowAnswer<ChoiceRow>;
export const isChoiceRows = (value: unknown): value is SavedChoiceRow[] =>
  Array.isArray(value) &&
  value.every((row: unknown) => {
    const answer = isSpecifiedRowAnswer(row) ? row.answer : row;
    return isChoiceValue(answer) || isChoiceList(answer);
  });

export const isTextAnswer = (value: unknown): value is string | string[] =>
  typeof value === "string" ||
  (Array.isArray(value) && value.every((item) => typeof item === "string"));
